/**
 * Which charts the Charts tab draws, and from which rows.
 *
 * The tab used to draw one line chart per table section — every section, with
 * whatever rows it held. Summit's analyst had asked for a specific set, the one
 * on the Graphs sheet of the KPI workbook he keeps by hand, and read the rest
 * as noise. This is that set, as data:
 *
 * - four fixed charts that each name their rows (occupancy, EGI vs NOI, NOI
 *   margin & DSCR, the leasing funnel),
 * - conversion rates between funnel stages, which the table does not carry as
 *   rows and which are worked out here from the stage totals it does,
 * - the operating-expense mix as shares of a total rather than lines over time,
 * - and the by-marketing-source sections, drawn as they always were.
 *
 * A fixed chart finds its rows by KEY, wherever the user's own arrangement of
 * the table has put them. A chart whose rows the workflow does not carry, or
 * carries with no numbers, is not drawn.
 *
 * Pure functions, so the rules are testable without the chart library.
 */

import {
	KPI_CUSTOM_GROUP_KEY,
	type KpiCell,
	type KpiMetricGroup,
	type KpiMetricRow,
	type KpiTable,
} from "../../types/kpi";
import { isEmptyMetricRow } from "./kpiEmptyRow";
import { valueAt } from "./kpiChartHeadline";

/** One share of the expense mix. `share` is a fraction of the ring's whole. */
export interface KpiMixSlice {
	key: string;
	label: string;
	value: number;
	share: number;
}

/** An expense line summed over the mix's periods. */
export interface KpiMixLine {
	key: string;
	label: string;
	value: number;
}

export interface KpiExpenseMix {
	/** The periods the totals cover, ascending: the latest calendar year reported. */
	periods: string[];
	/** Operating expenses net of credits — the table's own total where it has one. */
	total: number;
	/** Largest first. Shares are of expenses BEFORE credits, and sum to one. */
	slices: KpiMixSlice[];
	/** Lines that net to a credit (negative) over the periods: named, not drawn. */
	credits: KpiMixLine[];
}

export type KpiChart =
	| {
			kind: "trend";
			group: KpiMetricGroup;
			/** Plot every row, one axis per unit, instead of the dominant unit only. */
			mixedUnits: boolean;
	  }
	| { kind: "mix"; key: string; label: string; mix: KpiExpenseMix };

interface TrendSpec {
	key: string;
	label: string;
	/** Row key → the name the chart gives it. */
	series: readonly (readonly [string, string])[];
	mixedUnits?: boolean;
}

const OCCUPANCY: TrendSpec = {
	key: "occupancy_leasing",
	label: "Occupancy & Leasing",
	series: [
		["physical_occupancy", "Physical Occupancy Rate"],
		["economic_occupancy", "Economic Occupancy Rate"],
	],
};

const REVENUE: TrendSpec = {
	key: "revenue_trend",
	label: "Revenue Trend: EGI vs NOI",
	series: [
		["effective_gross_income", "EGI"],
		["net_operating_income", "NOI"],
	],
};

/* A percentage and a multiple: two axes, or the margin is a flat line at zero. */
const PROFITABILITY: TrendSpec = {
	key: "profitability",
	label: "Profitability: NOI Margin & DSCR",
	series: [
		["noi_margin", "NOI Margin"],
		["dscr", "DSCR"],
	],
	mixedUnits: true,
};

const FUNNEL: TrendSpec = {
	key: "leasing_funnel",
	label: "Leasing Funnel",
	series: [
		["leads_total", "Leads"],
		["tours_total", "Tours"],
		["applications_total", "Applications"],
		["leases_total", "Leases"],
	],
};

/** Stage-to-stage conversion: [key, label, numerator row, denominator row]. */
const CONVERSIONS: readonly (readonly [string, string, string, string])[] = [
	["conversion_lead_tour", "Lead → Tour", "tours_total", "leads_total"],
	[
		"conversion_tour_application",
		"Tour → Application",
		"applications_total",
		"tours_total",
	],
	[
		"conversion_application_lease",
		"Application → Lease",
		"leases_total",
		"applications_total",
	],
	["conversion_lead_lease", "Lead → Lease (overall)", "leases_total", "leads_total"],
];

/**
 * The expense lines that add up to total operating expenses. The per-unit and
 * ratio rows of the same section are a different measure of the same dollars,
 * and the two payroll halves are already inside `opex_payroll` (the server
 * fills it from them when a report states only the halves).
 */
const EXPENSE_LINES: readonly string[] = [
	"opex_payroll",
	"opex_administrative",
	"opex_marketing",
	"opex_repairs_maintenance",
	"opex_turnover",
	"opex_utilities",
	"opex_contract_services",
	"opex_insurance",
	"opex_property_taxes",
	"opex_management_fee",
	"opex_other",
];
const EXPENSE_TOTAL = "total_operating_expenses";
const NOT_ITEMIZED_KEY = "opex_not_itemized";
/** Below this share of the total, a gap between total and lines is rounding. */
const NOT_ITEMIZED_FLOOR = 0.005;

/** Table sections drawn as they are: one line per row of the section. */
const SECTION_CHARTS: readonly string[] = [
	"leads_by_source",
	"tours_by_source",
	"applications_by_source",
	"leases_by_source",
	"cost_per_lead_by_source",
	"cost_per_lease_by_source",
	KPI_CUSTOM_GROUP_KEY,
];

function rowsByKey(table: KpiTable): Map<string, KpiMetricRow> {
	const rows = new Map<string, KpiMetricRow>();
	table.groups.forEach((group) =>
		group.metrics.forEach((metric) => rows.set(metric.key, metric)),
	);
	return rows;
}

function trendChart(spec: TrendSpec, rows: Map<string, KpiMetricRow>): KpiChart | null {
	const metrics: KpiMetricRow[] = [];
	spec.series.forEach(([key, label]) => {
		const row = rows.get(key);
		if (row && !isEmptyMetricRow(row)) metrics.push({ ...row, label });
	});
	if (metrics.length === 0) return null;
	return {
		kind: "trend",
		group: { key: spec.key, label: spec.label, metrics },
		mixedUnits: spec.mixedUnits ?? false,
	};
}

function computedCell(value: number): KpiCell {
	return {
		value_id: null,
		value,
		effective_value: value,
		unit: "pct",
		confidence: "unverified",
		status: "active",
		derived: true,
	};
}

/**
 * Conversion between funnel stages, per period: the later stage's count over
 * the earlier one's. A ratio of the table's own (summed) counts, so it holds at
 * any grain and on the portfolio. No rate where either count is missing or the
 * earlier stage is zero.
 */
function conversionChart(
	table: KpiTable,
	rows: Map<string, KpiMetricRow>,
): KpiChart | null {
	const metrics: KpiMetricRow[] = [];
	CONVERSIONS.forEach(([key, label, numeratorKey, denominatorKey]) => {
		const numerator = rows.get(numeratorKey);
		const denominator = rows.get(denominatorKey);
		if (!numerator || !denominator) return;
		const cells: Record<string, KpiCell> = {};
		table.periods.forEach((period) => {
			const top = valueAt(numerator, period);
			const bottom = valueAt(denominator, period);
			if (top === null || bottom === null || bottom === 0) return;
			cells[period] = computedCell(top / bottom);
		});
		if (Object.keys(cells).length === 0) return;
		metrics.push({
			key,
			label,
			unit: "pct",
			aggregation: "recompute",
			is_raw: false,
			cells,
		});
	});
	if (metrics.length === 0) return null;
	return {
		kind: "trend",
		group: { key: "conversion_rates", label: "Conversion Rate Trends", metrics },
		mixedUnits: false,
	};
}

/**
 * Each expense line as a share of operating expenses over the latest calendar
 * year the table has expenses for — the full year where it has one, the months
 * so far where it does not.
 *
 * The shares are of TOTAL operating expenses where the table carries that row:
 * what the lines do not account for is its own slice rather than being spread
 * silently across the others. Without a total, the lines are shares of their
 * own sum.
 *
 * A line that nets to a credit has no share of a whole. It stays in `total`,
 * so the card's figure is the table's, and is listed in `credits`; the shares
 * are then of expenses before credits.
 */
export function expenseMix(table: KpiTable): KpiExpenseMix | null {
	const rows = rowsByKey(table);
	const expenseRows = EXPENSE_LINES.map((key) => rows.get(key)).filter(
		(row): row is KpiMetricRow => row !== undefined,
	);
	// The latest year with an expense in it: a table's columns can run past
	// its last reported month, and a year of empty columns has no mix.
	const latest = [...table.periods]
		.reverse()
		.find((period) => expenseRows.some((row) => valueAt(row, period) !== null));
	if (!latest) return null;
	const year = latest.slice(0, 4);
	// Only the periods that reported: the card names the span it covers.
	const periods = table.periods.filter(
		(period) =>
			period.slice(0, 4) === year &&
			expenseRows.some((row) => valueAt(row, period) !== null),
	);
	const sumOf = (row: KpiMetricRow | undefined): number | null => {
		if (!row) return null;
		let sum: number | null = null;
		periods.forEach((period) => {
			const value = valueAt(row, period);
			if (value !== null) sum = (sum ?? 0) + value;
		});
		return sum;
	};

	const lines: KpiMixLine[] = [];
	const credits: KpiMixLine[] = [];
	EXPENSE_LINES.forEach((key) => {
		const row = rows.get(key);
		const value = sumOf(row);
		if (!row || value === null || value === 0) return;
		// A credit cannot be drawn as a share of a whole; it is named instead.
		(value > 0 ? lines : credits).push({ key, label: row.label, value });
	});
	if (lines.length === 0) return null;

	const expenses = lines.reduce((sum, line) => sum + line.value, 0);
	const itemized = credits.reduce((sum, line) => sum + line.value, expenses);
	const stated = sumOf(rows.get(EXPENSE_TOTAL));
	const remainder = stated === null ? 0 : stated - itemized;
	const total = stated !== null && remainder >= 0 ? stated : itemized;
	// The whole the ring divides: expenses before credits.
	let gross = expenses;
	if (remainder > Math.abs(total) * NOT_ITEMIZED_FLOOR) {
		lines.push({ key: NOT_ITEMIZED_KEY, label: "Not itemized", value: remainder });
		gross += remainder;
	}

	return {
		periods,
		total,
		slices: lines
			.map((line) => ({ ...line, share: line.value / gross }))
			.sort((a, b) => b.value - a.value),
		credits,
	};
}

/**
 * The Charts tab, top to bottom. `table` is the table as the dashboard shows
 * it — in the layout's order, with the layout's names.
 */
export function kpiCharts(table: KpiTable): KpiChart[] {
	const rows = rowsByKey(table);
	const charts: (KpiChart | null)[] = [
		trendChart(OCCUPANCY, rows),
		trendChart(REVENUE, rows),
		trendChart(PROFITABILITY, rows),
	];

	const mix = expenseMix(table);
	charts.push(
		mix ? { kind: "mix", key: "expense_mix", label: "Operating Expense Mix", mix } : null,
	);
	charts.push(trendChart(FUNNEL, rows), conversionChart(table, rows));

	SECTION_CHARTS.forEach((sectionKey) => {
		const group = table.groups.find((candidate) => candidate.key === sectionKey);
		if (!group) return;
		const metrics = group.metrics.filter((metric) => !isEmptyMetricRow(metric));
		if (metrics.length === 0) return;
		charts.push({ kind: "trend", group: { ...group, metrics }, mixedUnits: false });
	});

	return charts.filter((chart): chart is KpiChart => chart !== null);
}
